import { text } from '#contract'
import type { LocalizedText, ProductView } from '#contract'
import { useT } from '../../../i18n/index.ts'
import styles from './products.module.css'
import { useShop } from './shop-context.ts'
import { ProductIcon, Stepper } from './stepper.tsx'

/** A product to list, with an optional second word under it (its aisle). */
export interface ProductEntry {
  readonly product: ProductView
  readonly detail?: LocalizedText
}

interface ProductListProps {
  readonly entries: readonly ProductEntry[]
  /** Cards in a grid (the desktop's aisles) instead of rows. */
  readonly grid?: boolean
}

/** Products as rows (icon, name, price, stepper) or as a grid of cards. */
export function ProductList({ entries, grid = false }: ProductListProps) {
  const Entry = grid ? ProductCard : ProductRow
  return (
    <div class={grid ? styles.grid : styles.rows}>
      {entries.map((entry) => (
        <Entry key={entry.product.item} {...entry} />
      ))}
    </div>
  )
}

function ProductRow({ product, detail }: ProductEntry) {
  const t = useT()
  const { money } = useShop()
  const price = money(product.priceCents)
  return (
    <div class={styles.row}>
      <span class={styles.pic}>
        <ProductIcon product={product} class={styles.picIcon} />
      </span>
      <span class={styles.info}>
        <span class={styles.name}>{t(product.name)}</span>
        <span class={styles.price}>
          {detail ? t(text('shop.priceIn', { price, section: detail })) : price}
        </span>
      </span>
      <Stepper product={product} />
    </div>
  )
}

function ProductCard({ product }: ProductEntry) {
  const t = useT()
  const { money } = useShop()
  return (
    <div class={styles.card}>
      <span class={styles.cardPic}>
        <ProductIcon product={product} class={styles.cardIcon} />
      </span>
      <span class={styles.name}>{t(product.name)}</span>
      <span class={styles.cardFoot}>
        <span class={styles.price}>{money(product.priceCents)}</span>
        <Stepper product={product} />
      </span>
    </div>
  )
}
